import {
  generateColorPalette,
  getBestColorForText,
} from '~/components/AccessibleColorPicker/color-palette-logic';
import type { RegistrationPage } from '~/graphql';
import { isNotNil } from '~/utilities/type-guards';
import omit from 'lodash/omit';
import { mergeBrandValuesIntoPage, getPersistedPageLevelCustomizations } from './utilities';
import { getColorsToExcludeFromText } from './colorGen';
import type {
  EventBrandValuesSet,
  GeneratedEventBrandValues,
  RegistrationCustomizationsInheritedFromBrand,
} from '../Brand/types';
import { RegistrationPageMappedBrandValues } from '../Brand/types';

export const getRegistrationPageCustomizationsForPreview = (
  brand: GeneratedEventBrandValues,
  registrationPageCustomizations: RegistrationPage,
): RegistrationCustomizationsInheritedFromBrand => {
  const updatedRegistrationPageCustomizations = mergeBrandValuesIntoPage<RegistrationPage>(
    registrationPageCustomizations,
    brand,
    RegistrationPageMappedBrandValues,
  );

  if (isNotNil(registrationPageCustomizations.heroImageUnsplashUrl)) {
    updatedRegistrationPageCustomizations.heroImageUrl =
      registrationPageCustomizations.heroImageUnsplashUrl;
  }

  // If the Registration Page has its own background color, generate the contrasting text colors
  if (isNotNil(registrationPageCustomizations.backgroundColor)) {
    const registrationPageColorPalette = generateColorPalette([
      updatedRegistrationPageCustomizations.backgroundColor,
      updatedRegistrationPageCustomizations.primaryColor,
      updatedRegistrationPageCustomizations.slightAccentedBackgroundColor,
      updatedRegistrationPageCustomizations.heavyAccentedBackgroundColor,
    ]);

    updatedRegistrationPageCustomizations.mainTextColor = getBestColorForText(
      registrationPageColorPalette,
      getColorsToExcludeFromText([updatedRegistrationPageCustomizations.primaryColor]),
      registrationPageCustomizations.backgroundColor,
    );

    updatedRegistrationPageCustomizations.headingTextColor = getBestColorForText(
      registrationPageColorPalette,
      getColorsToExcludeFromText([updatedRegistrationPageCustomizations.primaryColor]),
      registrationPageCustomizations.backgroundColor,
    );
  }

  return updatedRegistrationPageCustomizations;
};

export const getPersistedRegistrationPageCustomizations = (
  brand: EventBrandValuesSet,
  registrationPageCustomizations: RegistrationCustomizationsInheritedFromBrand,
  shouldOmitImageUrls = false,
): RegistrationPage => {
  // Make a shallow copy so we don't update any arguments passed in as reference
  const updatedRegistrationPageCustomizations = { ...registrationPageCustomizations };

  const persistedRegistrationPageCustomizations =
    getPersistedPageLevelCustomizations<RegistrationPage>(
      brand,
      updatedRegistrationPageCustomizations,
      RegistrationPageMappedBrandValues,
      shouldOmitImageUrls ? ['logoUrl'] : [],
    );

  const keysToOmit: (keyof RegistrationPage)[] = shouldOmitImageUrls ? ['heroImageUrl'] : [];

  return omit(persistedRegistrationPageCustomizations, keysToOmit) as RegistrationPage;
};
